import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextareaAutosize,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks'
import { useProductSettings } from '../../hooks'
import {
  BillItem,
  ITables,
  KITCHEN_ITEM_STATUS,
  ProductType,
  RateCard,
  TABLE_STATUS,
} from '../../all-types'
import { defaultBillItem } from '../../constants'
import { addTableItem, updateTableItem } from '../../services'
import { CommonBtn } from '../../components'
import { setSelectedTable } from '../../redux-store'
import { MODIFY_BILL_TABLE_ID, TAKE_AWAY_TABLE_ID } from '../../constants'

interface ProductPopsProps {
  product: ProductType
  currentTab: string
  editIndex: number | null
  exit: () => void
}

const getDefaultBillRecord = (product: ProductType): BillItem => {
  return {
    ...defaultBillItem,
    productId: product.productId,
    itemName: product.name,
    itemImage: product.image,
  }
}

const ProductPopup: React.FC<ProductPopsProps> = ({
  product,
  exit,
  editIndex,
}) => {
  const theme = useTheme()
  const dispatcher = useAppDispatch()
  const table = useAppSelector((state) => state.table.selectedTable)
  const items = table?.billSession?.items
  const isTakeAwayTable = table?._id === TAKE_AWAY_TABLE_ID
  const isModifyBill =
    table?._id === MODIFY_BILL_TABLE_ID || table?.tableName === 'Modify Bill'

  const tempRecord =
    editIndex === null
      ? getDefaultBillRecord(product)
      : items
        ? items[editIndex]
        : getDefaultBillRecord(product)
  const { billRecord, setBillRecord } = useProductSettings(product, tempRecord)
  const [rateCardId, setSetRateCardId] = useState(billRecord.rateCardName)
  const [openHeadings, setOpenHeadings] = useState<{ [key: string]: boolean }>(
    {}
  )
  const [selectedInst, setSelectedInst] = useState<{ [key: string]: string }>(
    billRecord?.kitchInfo?.kitchenInstrcutions
      ? JSON.parse(billRecord?.kitchInfo?.kitchenInstrcutions)
      : {}
  )
  const [submitting, setSubmitting] = useState(false)
  const hasAdditionalOptions =
    Object.entries(product?.productExtras)?.length > 0 ||
    Object.entries(product?.productInstructions)?.length > 0

  const toggleHeading = (heading: string) => {
    setOpenHeadings((prev) => ({
      ...prev,
      [heading]: !prev[heading],
    }))
  }

  const onSelectOfRateCard = (rc: RateCard) => {
    setBillRecord({
      ...billRecord,
      mrp: rc.price,
      noOfItems: 1,
      amount: rc.price,
      quantity: rc.quantity,
      quantityType: rc.quantityType,
      rateCardName: rc.name,
    })
    setSetRateCardId(rc.name) //KS:CHANGE
  }

  const onSelectOfCustomRateCard = () => {
    setBillRecord({
      ...billRecord,
      mrp: 0,
      noOfItems: 1,
      amount: 0,
      quantity: 0,
      quantityType: '',
      rateCardName: 'CUSTOM',
    })
    setSetRateCardId('CUSTOM') //KS:CHANGE
  }

  const getCustomPrice = (
    quantity = billRecord.quantity,
    type = billRecord.quantityType,
    noOfItems = billRecord.noOfItems
  ) => {
    try {
      if (product.defaults && Object.entries(product.defaults)) {
        const productDefaults = new Map(Object.entries(product.defaults))
        if (type && productDefaults.has(type)) {
          const typeRate = productDefaults.get(type)
          return Number(
            (Number(typeRate?.price || 1) * quantity * noOfItems).toFixed(4)
          )
        }
      }
    } catch (e) {
      console.error(e)
    }
    return billRecord.amount
  }

  const setCustomType = (type: string) => {
    setBillRecord({
      ...billRecord,
      amount: getCustomPrice(billRecord.quantity, type),
      quantityType: type,
    })
  }

  const setCustomQuantity = (quantity: number) => {
    setBillRecord({
      ...billRecord,
      amount: getCustomPrice(quantity, billRecord.quantityType),
      quantity: quantity,
    })
  }

  const incrementItem = () => {
    const noOfItems = billRecord.noOfItems + 1
    let newAmount = 0
    if (rateCardId === 'CUSTOM') {
      newAmount = getCustomPrice(
        billRecord.quantity,
        billRecord.quantityType,
        noOfItems
      )
    } else {
      newAmount = noOfItems * billRecord.mrp
    }
    setBillRecord({
      ...billRecord,
      amount: newAmount,
      noOfItems: noOfItems,
    })
  }

  const decrementItem = () => {
    //const newAmount = (billRecord.noOfItems - 1) * (billRecord.mrp);
    const noOfItems = billRecord.noOfItems - 1
    let newAmount = 0
    if (rateCardId === 'CUSTOM') {
      newAmount = getCustomPrice(
        billRecord.quantity,
        billRecord.quantityType,
        noOfItems
      )
    } else {
      newAmount = noOfItems * billRecord.mrp
    }
    setBillRecord({
      ...billRecord,
      amount: newAmount,
      noOfItems: billRecord.noOfItems - 1,
    })
  }

  const isDefaultRatesExists =
    Object.values(product.defaults) &&
    Object.values(product.defaults).length > 0

  const onSelectInstructions = (heading: string, inst: string) => {
    setSelectedInst({ ...selectedInst, [heading]: inst })
  }

  const addItemToCart = async () => {
    if (submitting) return
    setSubmitting(true)
    try {
      if (table) {
        const clonedTable: ITables = JSON.parse(JSON.stringify(table))
        clonedTable.status = TABLE_STATUS.OCCUPIED
        const billSession = clonedTable.billSession

        const isEdit =
          editIndex !== null &&
          editIndex >= 0 &&
          !!billSession?.items?.[editIndex]

        // Only this single item is sent to the server now, so other items'
        // kitchen status can no longer be overwritten by a stale snapshot.
        const editedItem: BillItem = isEdit
          ? {
              ...billRecord,
              kitchInfo: {
                ...billRecord.kitchInfo,
                kitchenInstrcutions: JSON.stringify(selectedInst),
              },
            }
          : {
              ...billRecord,
              sno: new Date().getTime(),
              kitchInfo: {
                status:
                  isTakeAwayTable && !isModifyBill
                    ? KITCHEN_ITEM_STATUS.IN_KITCHEN
                    : isModifyBill
                      ? KITCHEN_ITEM_STATUS.SERVED
                      : KITCHEN_ITEM_STATUS.IN_CART,
                kitchenId: '',
                kitchenInstrcutions: JSON.stringify(selectedInst),
                itemReadyTime: String(new Date().getTime()),
                itemReceviedTime: '',
                itemServedTime: '',
              },
            }

        // Build the optimistic local snapshot so the cart updates instantly.
        if (billSession) {
          const items = [...(billSession.items ?? [])]
          if (isEdit) {
            items[editIndex as number] = editedItem
          } else {
            items.push(editedItem)
          }
          clonedTable.billSession = {
            ...billSession,
            items,
          }
        }

        if (isTakeAwayTable || isModifyBill) {
          await dispatcher(setSelectedTable(clonedTable))
        } else {
          dispatcher(setSelectedTable(clonedTable))
          exit()
          if (isEdit) {
            dispatcher(
              updateTableItem({
                tableId: table._id || '',
                sno: editedItem.sno,
                item: editedItem,
              })
            ).then((result) => {
              if (updateTableItem.rejected.match(result)) {
                const payload = (result as any).payload
                if (payload?.locked && payload.lockInfo) {
                  window.alert(
                    `This table is currently open by ${payload.lockInfo.lockedByName}.`
                  )
                } else {
                  window.alert(
                    'Unable to update table. Please refresh and try again.'
                  )
                }
              }
            })
          } else {
            dispatcher(
              addTableItem({
                tableId: table._id || '',
                item: editedItem,
                billSession: clonedTable.billSession,
              })
            ).then((result) => {
              if (addTableItem.rejected.match(result)) {
                const payload = (result as any).payload
                if (payload?.locked && payload.lockInfo) {
                  window.alert(
                    `This table is currently open by ${payload.lockInfo.lockedByName}.`
                  )
                } else {
                  window.alert(
                    'Unable to update table. Please refresh and try again.'
                  )
                }
              }
            })
          }
          return
        }
      }
      exit()
    } catch (e) {
      console.error(e)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        p: 1,
        backgroundColor: theme.palette.background.default,
        maxHeight: '95vh', // adjust as needed
        overflowY: 'auto',
      }}
    >
      <CardContent>
        <Grid
          container
          spacing={2}
          alignItems={'center'}
          display={'flex'}
          flexDirection={'row'}
        >
          {/* LEFT SIDE: Product & Quantity Controls */}
          <Grid item xs={12} md={6} alignSelf={'flex-start'}>
            <Stack flexDirection={'row'}>
              <Stack p={0.5}>
                <Box
                  component="img"
                  height={150}
                  width={150}
                  src={product.image}
                  alt={product.name}
                  style={{ borderRadius: 8 }}
                />
              </Stack>
              <Stack
                p={0.5}
                height={165}
                display={'flex'}
                justifyContent={'space-between'}
              >
                <Box>
                  <Typography component="div" variant="h5">
                    {product.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {product.description}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    flexDirection={'row'}
                  >
                    <Typography
                      component={'span'}
                      variant="body2"
                      fontWeight={'bold'}
                    >
                      Product Id:{' '}
                    </Typography>
                    {product.productCode}
                  </Typography>
                  {/* <Typography variant="h6">MRP ₹300</Typography> */}
                </Box>
                <Grid item xs={6} display={'flex'} alignItems={'flex-end'}>
                  <Stack direction="row" gap={2} alignItems={'center'}>
                    <Button
                      variant="outlined"
                      sx={{ fontSize: 25, borderRadius: 15 }}
                      disabled={billRecord.noOfItems === 1}
                      onClick={decrementItem}
                    >
                      -
                    </Button>
                    <Typography variant="h6">{billRecord.noOfItems}</Typography>
                    <Button
                      variant="contained"
                      sx={{ fontSize: 25, borderRadius: 15 }}
                      onClick={incrementItem}
                    >
                      +
                    </Button>
                  </Stack>
                </Grid>
              </Stack>
            </Stack>
            <Typography variant="body1">Select Quantity</Typography>
            <Stack direction="row" gap={1} flexWrap="wrap" mt={1}>
              {Object.values(product.rateCard)?.map((i: RateCard) => (
                <CommonBtn
                  key={i.name}
                  variant={rateCardId === i.name ? 'contained' : 'outlined'}
                  sx={{ height: 50 }}
                  onClick={() => onSelectOfRateCard(i)}
                >
                  {i.name}
                </CommonBtn>
              ))}
              {isDefaultRatesExists && (
                <CommonBtn
                  key={'custom-btn'}
                  variant={rateCardId === 'CUSTOM' ? 'contained' : 'outlined'}
                  sx={{ height: 50 }}
                  onClick={onSelectOfCustomRateCard}
                >
                  Custom
                </CommonBtn>
              )}
            </Stack>

            {rateCardId === 'CUSTOM' && (
              <Stack mt={2} gap={1}>
                <Typography variant="body1">Customize</Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #ccc',
                    borderRadius: 2,
                  }}
                >
                  <TextField
                    value={billRecord.quantity}
                    onChange={(e) => setCustomQuantity(Number(e.target.value))}
                    placeholder="ex: 125"
                    sx={{
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                      flex: 1,
                    }}
                    variant="outlined"
                  />
                  <Select
                    value={billRecord.quantityType}
                    onChange={(e) => setCustomType(e.target.value)}
                    sx={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      flex: 0.5,
                    }}
                    variant="outlined"
                  >
                    {Object.values(product.defaults)?.map((unit: RateCard) => (
                      <MenuItem key={unit.name} value={unit.quantityType}>
                        {unit.quantityType}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Stack>
            )}
          </Grid>

          {/* RIGHT SIDE: Extras & Instructions */}

          <Grid item xs={12} md={6} alignSelf={'flex-start'}>
            <Stack spacing={1.5}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                Additional Instructions
              </Typography>

              <TextareaAutosize
                minRows={4}
                placeholder="Enter additional instructions here..."
                onChange={(e) =>
                  setSelectedInst({
                    ...selectedInst,
                    ['Additional']: e.target.value,
                  })
                }
                value={selectedInst['Additional'] || ''}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  lineHeight: 1.5,
                  resize: 'vertical',
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.paper,
                }}
              />
            </Stack>
            {Object.entries(product.productExtras)?.length > 0 && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Extras
                </Typography>
                {Object.entries(product.productExtras)?.map(
                  ([heading, extras]) => (
                    <Stack
                      key={heading}
                      gap={1}
                      sx={{
                        border: `1px solid #ddd`,
                        borderRadius: 2,
                        p: 2,
                        mt: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        onClick={() => toggleHeading(heading)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <Typography variant="body2">{heading}</Typography>
                        <IconButton size="small">
                          {openHeadings[heading] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </Stack>
                      <Collapse
                        in={openHeadings[heading]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Stack direction="row" gap={1} flexWrap="wrap" mt={1}>
                          {extras?.map((extra) => (
                            <CommonBtn key={extra.extraName} variant="outlined">
                              {extra.extraName}
                            </CommonBtn>
                          ))}
                        </Stack>
                      </Collapse>
                    </Stack>
                  )
                )}
              </>
            )}

            {/* INSTRUCTIONS */}

            {Object.entries(product.productInstructions)?.length > 0 && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 600, mt: 2 }}>
                  Instructions
                </Typography>
                {Object.entries(product.productInstructions)?.map(
                  ([heading, instructions]) => (
                    <Stack
                      key={heading}
                      gap={1}
                      sx={{
                        border: '1px solid #ddd',
                        borderRadius: 2,
                        p: 2,
                        mt: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        onClick={() => toggleHeading(heading)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <Typography variant="body2">{heading}</Typography>
                        <IconButton size="small">
                          {openHeadings[heading] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </Stack>
                      <Collapse
                        in={openHeadings[heading]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Stack direction="row" gap={1} flexWrap="wrap" mt={1}>
                          {instructions?.map((inst) => (
                            <CommonBtn
                              key={inst}
                              variant={
                                selectedInst[heading] &&
                                selectedInst[heading] === inst
                                  ? 'contained'
                                  : 'outlined'
                              }
                              onClick={() =>
                                onSelectInstructions(heading, inst)
                              }
                            >
                              {inst}
                            </CommonBtn>
                          ))}
                        </Stack>
                      </Collapse>
                    </Stack>
                  )
                )}
              </>
            )}
          </Grid>
        </Grid>

        {/* PRICE ROW */}
        <Grid container mt={3}>
          <Grid item xs={12} display="flex" justifyContent="center">
            <Stack direction="row" gap={2} alignItems="center">
              <Typography
                variant="h6"
                fontWeight="bold"
                color={theme.palette.primary.main}
              >
                {billRecord.noOfItems}x
              </Typography>
              <Typography
                variant="h6"
                fontWeight="bold"
                color={theme.palette.primary.main}
              >
                Qty
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {billRecord.quantity} {billRecord.quantityType}
              </Typography>
              <Typography
                variant="h6"
                fontWeight="bold"
                color={theme.palette.primary.main}
              >
                PRICE
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                ₹{billRecord.amount}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 2,
          gap: 2,
        }}
      >
        <CommonBtn
          variant="outlined"
          color="error"
          sx={{ fontSize: 25 }}
          onClick={exit}
          disabled={submitting}
        >
          Cancel
        </CommonBtn>
        <CommonBtn
          variant="contained"
          disabled={billRecord.amount === 0 || submitting}
          sx={{ fontSize: 25 }}
          onClick={addItemToCart}
          startIcon={
            submitting ? <CircularProgress color="inherit" size={22} /> : undefined
          }
        >
          {submitting ? 'Adding...' : 'Done'}
        </CommonBtn>
      </Box>
    </Card>
  )
}

export { ProductPopup }
